import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  PencilSquareIcon,
  PlusIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { useShop } from '../contexts/ShopContext';
import { useToast } from '../contexts/ToastContext';
import { IN_STORE_HINT, describeLineCounts, readBreakdown } from '../lib/lineCounts';
import {
  useProductLines,
  type CatalogCounts,
  type LineDraft,
  type LinePreview,
  type ModePreview,
  type ProductLine,
  type SelectionMode,
} from '../hooks/useProductLines';
import ProductApprovalQueue from './ProductApprovalQueue';

/**
 * Product lines: pick the brands and models you sell, and FlowStock finds them across
 * every connected supplier — now and as suppliers add new products.
 *
 * `catalog` (onboarding, before any supplier is connected): brands, previews and counts
 * cover every active supplier, and the per-supplier selection switch is left to the wizard.
 */

const EMPTY_DRAFT: LineDraft = {
  name: '',
  tag: '',
  brand: null,
  patterns: [],
  exclude_patterns: [],
  footwear_only: true,
  provider_ids: null,
  approval_mode: 'auto',
  create_collection: true,
};

const splitList = (v: string) =>
  v.split(',').map((s) => s.trim()).filter(Boolean);

const ruleSummary = (line: ProductLine, brandLabel: (b: string) => string, providerName: (id: string) => string) => {
  const parts: string[] = [];
  if (line.brand) parts.push(`Brand: ${brandLabel(line.brand)}`);
  if (line.patterns.length) parts.push(`Model: ${line.patterns.join(' / ')}`);
  if (line.exclude_patterns.length) parts.push(`Not: ${line.exclude_patterns.join(' / ')}`);
  if (line.footwear_only) parts.push('Footwear only');
  parts.push(line.provider_ids?.length ? line.provider_ids.map(providerName).join(', ') : 'All suppliers');
  return parts.join(' · ');
};

interface ProductLinesProps {
  catalog?: boolean;
  onLinesChange?: (lines: ProductLine[], counts: CatalogCounts | null) => void;
}

const ProductLines: React.FC<ProductLinesProps> = ({ catalog = false, onLinesChange }) => {
  const { currentShopId } = useShop();
  const toast = useToast();
  const pl = useProductLines(currentShopId, { catalog });

  useEffect(() => {
    if (pl.loaded) onLinesChange?.(pl.lines, pl.counts);
  }, [pl.loaded, pl.lines, pl.counts, onLinesChange]);

  const [editing, setEditing] = useState<LineDraft | null>(null);
  const [tagTouched, setTagTouched] = useState(false);
  const [nameTouched, setNameTouched] = useState(false);
  const [preview, setPreview] = useState<LinePreview | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<ProductLine | null>(null);
  const [modeConfirm, setModeConfirm] = useState<ModePreview | null>(null);
  const [modeBusy, setModeBusy] = useState<SelectionMode | null>(null);
  const previewSeq = useRef(0);

  // Brands are only needed by the editor; load them on first open.
  const { brands, loadBrands } = pl;
  useEffect(() => {
    if (editing && brands.length === 0) {
      loadBrands().catch((e) => toast.error(e.message));
    }
  }, [editing, brands.length, loadBrands, toast]);

  const brandLabel = useMemo(() => {
    const map = new Map(brands.map((b) => [b.brand, b.label]));
    return (key: string) => map.get(key) ?? key;
  }, [brands]);
  const providerName = useMemo(() => {
    const map = new Map(pl.providers.map((p) => [p.id, p.name]));
    return (id: string) => map.get(id) ?? 'Unknown supplier';
  }, [pl.providers]);

  // Live preview of the line being edited, debounced; stale answers are dropped.
  const { preview: runPreview } = pl;
  useEffect(() => {
    if (!editing || (!editing.brand && editing.patterns.length === 0)) {
      setPreview(null);
      setPreviewError(null);
      return;
    }
    const seq = ++previewSeq.current;
    setPreviewing(true);
    const t = setTimeout(async () => {
      try {
        const result = await runPreview(editing);
        if (seq === previewSeq.current) {
          setPreview(result);
          setPreviewError(null);
        }
      } catch (e: any) {
        if (seq === previewSeq.current) setPreviewError(e.message);
      } finally {
        if (seq === previewSeq.current) setPreviewing(false);
      }
    }, 600);
    return () => clearTimeout(t);
  }, [editing, runPreview]);

  const deselectedCount = pl.providers.filter((p) => p.selection_mode === 'deselect_all').length;

  const update = (patch: Partial<LineDraft>) => {
    setEditing((d) => {
      if (!d) return d;
      const next = { ...d, ...patch };
      if (!next.id) {
        // Name and tag follow the rule until the merchant types their own.
        const autoName = [next.brand ? brandLabel(next.brand) : '', next.patterns[0] ?? '']
          .filter(Boolean).join(' ');
        if (!nameTouched && !('name' in patch)) next.name = autoName;
        if (!tagTouched && !('tag' in patch)) next.tag = next.name;
      }
      return next;
    });
  };

  const openNew = () => {
    setTagTouched(false);
    setNameTouched(false);
    setPreview(null);
    setEditing({ ...EMPTY_DRAFT });
  };

  const openEdit = (line: ProductLine) => {
    setTagTouched(true);
    setNameTouched(true);
    setEditing({
      id: line.id,
      name: line.name,
      tag: line.tag,
      brand: line.brand,
      patterns: line.patterns,
      exclude_patterns: line.exclude_patterns,
      footwear_only: line.footwear_only,
      provider_ids: line.provider_ids,
      approval_mode: line.approval_mode ?? 'auto',
      create_collection: !line.collection_id,
    });
  };

  const save = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      const result = await pl.save(editing);
      const collection = result.collection_error
        ? ` Collection not created: ${result.collection_error}`
        : result.collection_id
          ? result.collection_created ? ' Collection created.' : ' Linked to your existing collection.'
          : '';
      toast.success(catalog
        ? `Saved "${editing.name}".${collection}`
        : `Saved "${editing.name}" — ${result.matched} products match.${collection}`);
      setEditing(null);
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    try {
      await pl.remove(pendingDelete.id);
      toast.success(`Removed "${pendingDelete.name}"`);
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setPendingDelete(null);
    }
  };

  const askMode = async (mode: SelectionMode) => {
    setModeBusy(mode);
    try {
      setModeConfirm(await pl.previewMode(mode));
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setModeBusy(null);
    }
  };

  const applyMode = async () => {
    if (!modeConfirm) return;
    try {
      const { providers_changed } = await pl.setMode(modeConfirm.mode);
      toast.success(`Updated ${providers_changed} supplier${providers_changed === 1 ? '' : 's'}. Your store updates on the next sync.`);
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setModeConfirm(null);
    }
  };

  const retryCollection = async (line: ProductLine) => {
    try {
      const r = await pl.createCollection(line.id);
      if (r.collection_error) toast.error(r.collection_error);
      else toast.success(`Collection ready for "${line.name}"`);
    } catch (e: any) {
      toast.error(e.message);
    }
  };

  if (!currentShopId) {
    return (
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-16">
        <p className="text-white/60">Select a shop first.</p>
      </main>
    );
  }

  return (
    <div className="min-h-screen">
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-16 space-y-6">
        {/* What the store sells (onboarding asks this itself) */}
        {!catalog && (
        <section className="glass-card rounded-xl p-6 space-y-4">
          <div>
            <h2 className="text-white font-semibold text-lg">What your store sells</h2>
            <p className="text-white/60 text-sm mt-1">
              {pl.providers.length === 0
                ? 'No suppliers connected yet.'
                : deselectedCount === 0
                  ? `All ${pl.providers.length} suppliers: everything they carry.`
                  : deselectedCount === pl.providers.length
                    ? `All ${pl.providers.length} suppliers: only your product lines and products you picked by hand.`
                    : `${deselectedCount} of ${pl.providers.length} suppliers: only your product lines and hand-picked products. The others: everything.`}
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <button
              onClick={() => askMode('deselect_all')}
              disabled={modeBusy !== null || pl.providers.length === 0}
              className="text-left p-4 rounded-lg border border-white/10 hover:border-orange-500/60 hover:bg-white/[0.04] disabled:opacity-50"
            >
              <div className="text-white font-medium">{modeBusy === 'deselect_all' ? 'Checking…' : 'Deselect all'}</div>
              <div className="text-white/50 text-xs mt-1">
                Sell only products matching your product lines (plus anything you ticked in a
                supplier catalog). Everything else is set to 0 stock and drafted.
              </div>
            </button>
            <button
              onClick={() => askMode('select_all')}
              disabled={modeBusy !== null || pl.providers.length === 0}
              className="text-left p-4 rounded-lg border border-white/10 hover:border-orange-500/60 hover:bg-white/[0.04] disabled:opacity-50"
            >
              <div className="text-white font-medium">{modeBusy === 'select_all' ? 'Checking…' : 'Select all'}</div>
              <div className="text-white/50 text-xs mt-1">
                Sell everything your suppliers carry. Product lines still tag and collect
                their products.
              </div>
            </button>
          </div>
        </section>
        )}

        {/* Lines */}
        <section className="glass-card rounded-xl p-6 space-y-4">
          <div className="flex items-center justify-between gap-4">
            <div>
              <h2 className="text-white font-semibold text-lg">Product lines</h2>
              <p className="text-white/60 text-sm mt-1">
                A brand ("UGG") or a model ("Nike P-6000"). Matching products from every
                supplier are imported and tagged, including ones suppliers add later.
              </p>
            </div>
            <button
              onClick={openNew}
              className="flex items-center gap-1.5 px-4 py-2 text-sm bg-orange-600 text-white rounded hover:bg-orange-700 shrink-0"
            >
              <PlusIcon className="w-4 h-4" /> Add product line
            </button>
          </div>

          {pl.error && <p className="text-red-400 text-sm">{pl.error}</p>}
          {!pl.loaded && <p className="text-white/50 text-sm">Loading…</p>}
          {pl.loaded && pl.lines.length === 0 && !pl.error && (
            <p className="text-white/50 text-sm">No product lines yet.</p>
          )}

          <ul className="divide-y divide-white/10">
            {pl.lines.map((line) => (
              <li key={line.id} className="py-3 flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-white font-medium">{line.name}</span>
                    <span className="text-xs px-2 py-0.5 rounded bg-orange-500/15 text-orange-300">
                      tag: {line.tag}
                    </span>
                    {line.approval_mode === 'approval' && (
                      <span className="text-xs px-2 py-0.5 rounded bg-sky-500/15 text-sky-300">
                        waits for approval
                      </span>
                    )}
                  </div>
                  <div className="text-white/50 text-xs mt-1 truncate">
                    {ruleSummary(line, brandLabel, providerName)}
                  </div>
                  <div className="text-xs mt-1 flex items-center gap-3 flex-wrap">
                    {catalog ? (
                      <span className="text-white/70">
                        {!pl.counts ? 'Counting…' : `${pl.counts.lines[line.id]?.products ?? 0} products at ${pl.counts.lines[line.id]?.suppliers ?? 0} supplier${pl.counts.lines[line.id]?.suppliers === 1 ? '' : 's'}`}
                      </span>
                    ) : (
                      <span className="text-white/70" title={readBreakdown(line.match_breakdown)?.in_store ? IN_STORE_HINT : undefined}>
                        {describeLineCounts(readBreakdown(line.match_breakdown), line.matched_count)}
                      </span>
                    )}
                    {line.collection_id ? (
                      <span className="flex items-center gap-1 text-emerald-400">
                        <CheckCircleIcon className="w-3.5 h-3.5" /> Collection
                      </span>
                    ) : line.collection_error ? (
                      <button
                        onClick={() => retryCollection(line)}
                        title={line.collection_error}
                        className="flex items-center gap-1 text-amber-400 hover:underline"
                      >
                        <ExclamationTriangleIcon className="w-3.5 h-3.5" /> Collection failed — retry
                      </button>
                    ) : (
                      <button onClick={() => retryCollection(line)} className="text-white/50 hover:text-white hover:underline">
                        Create collection
                      </button>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button onClick={() => openEdit(line)} className="p-2 text-white/60 hover:text-white" title="Edit">
                    <PencilSquareIcon className="w-4 h-4" />
                  </button>
                  <button onClick={() => setPendingDelete(line)} className="p-2 text-white/60 hover:text-red-400" title="Remove">
                    <TrashIcon className="w-4 h-4" />
                  </button>
                </div>
              </li>
            ))}
          </ul>

          <p className="text-white/40 text-xs">
            On Shopify each line gets a smart collection with the rule "Product tag is equal
            to {'<tag>'}" (an existing collection with that rule is reused). On WooCommerce the
            tag is also assigned as a product category.
          </p>
        </section>

        {!catalog && pl.loaded && (pl.lines.some((l) => l.approval_mode === 'approval') || pl.approvals.pending > 0 || pl.approvals.rejected > 0) && (
          <ProductApprovalQueue api={pl} version={pl.lines} />
        )}
      </main>

      {/* Dialogs render into document.body: inside the onboarding wizard this component sits under a
          transformed (animated) container, which makes position:fixed relative to that container and
          left the editor clipped and see-through inside the wizard card. */}
      {/* Editor */}
      {editing && createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 overflow-y-auto py-8">
          <div className="glass-card rounded-xl p-6 w-full max-w-2xl mx-4 space-y-4">
            <h3 className="text-white font-semibold text-lg">
              {editing.id ? 'Edit product line' : 'Add product line'}
            </h3>

            <label className="block">
              <span className="text-white/70 text-sm">Brand</span>
              <input
                list="product-line-brands"
                value={editing.brand ? brandLabel(editing.brand) : ''}
                onChange={(e) => {
                  const v = e.target.value.trim();
                  const hit = brands.find((b) => b.label.toLowerCase() === v.toLowerCase() || b.brand === v.toLowerCase());
                  update({ brand: v ? (hit?.brand ?? v) : null });
                }}
                placeholder="Any brand"
                className="mt-1 w-full px-3 py-2 rounded bg-white/[0.04] border border-white/10 text-white text-sm"
              />
              <datalist id="product-line-brands">
                {brands.map((b) => (
                  <option key={b.brand} value={b.label}>{b.products} products</option>
                ))}
              </datalist>
            </label>

            <label className="block">
              <span className="text-white/70 text-sm">Model (optional — leave empty for the whole brand)</span>
              <input
                defaultValue={editing.patterns.join(', ')}
                onChange={(e) => update({ patterns: splitList(e.target.value) })}
                placeholder="e.g. P-6000   ·   separate spellings with commas: Jordan 1 Low, Jordan 1 Retro Low"
                className="mt-1 w-full px-3 py-2 rounded bg-white/[0.04] border border-white/10 text-white text-sm"
              />
              <span className="text-white/40 text-xs">
                Case, spaces and dashes don't matter: "P6000" also finds "P-6000" and "Nike Initiator P-6000".
              </span>
            </label>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <label className="block">
                <span className="text-white/70 text-sm">Name</span>
                <input
                  value={editing.name}
                  onChange={(e) => {
                    setNameTouched(true);
                    update({ name: e.target.value });
                  }}
                  className="mt-1 w-full px-3 py-2 rounded bg-white/[0.04] border border-white/10 text-white text-sm"
                />
              </label>
              <label className="block">
                <span className="text-white/70 text-sm">Store tag</span>
                <input
                  value={editing.tag}
                  onChange={(e) => {
                    setTagTouched(true);
                    update({ tag: e.target.value });
                  }}
                  className="mt-1 w-full px-3 py-2 rounded bg-white/[0.04] border border-white/10 text-white text-sm"
                />
              </label>
            </div>

            <label className="block">
              <span className="text-white/70 text-sm">Exclude (optional)</span>
              <input
                defaultValue={editing.exclude_patterns.join(', ')}
                onChange={(e) => update({ exclude_patterns: splitList(e.target.value) })}
                placeholder="e.g. Samba Jane — keeps them out of an Adidas Samba line"
                className="mt-1 w-full px-3 py-2 rounded bg-white/[0.04] border border-white/10 text-white text-sm"
              />
            </label>

            {!catalog && (
            <div className="space-y-2">
              <span className="text-white/70 text-sm">Suppliers</span>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => update({ provider_ids: null })}
                  className={`px-3 py-1 text-xs rounded border ${!editing.provider_ids ? 'border-orange-500 text-white' : 'border-white/15 text-white/60'}`}
                >
                  All suppliers
                </button>
                {pl.providers.map((p) => {
                  const on = editing.provider_ids?.includes(p.id) ?? false;
                  return (
                    <button
                      key={p.id}
                      onClick={() => {
                        const cur = new Set(editing.provider_ids ?? []);
                        if (on) cur.delete(p.id); else cur.add(p.id);
                        update({ provider_ids: cur.size ? Array.from(cur) : null });
                      }}
                      className={`px-3 py-1 text-xs rounded border ${on ? 'border-orange-500 text-white' : 'border-white/15 text-white/60'}`}
                    >
                      {p.name}
                    </button>
                  );
                })}
              </div>
            </div>
            )}

            <div className="space-y-2">
              <span className="text-white/70 text-sm">When a supplier adds a new matching product</span>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {([
                  ['auto', 'Add automatically', 'Imported at the next sync.'],
                  ['approval', 'Wait for approval', 'Waits in an approval list on this page until you approve it.'],
                ] as const).map(([mode, label, help]) => (
                  <button
                    key={mode}
                    onClick={() => update({ approval_mode: mode })}
                    className={`text-left p-3 rounded border ${(editing.approval_mode ?? 'auto') === mode ? 'border-orange-500' : 'border-white/15'}`}
                  >
                    <div className="text-white text-sm">{label}</div>
                    <div className="text-white/50 text-xs mt-0.5">{help}</div>
                  </button>
                ))}
              </div>
              {editing.approval_mode === 'approval' && (
                <p className="text-white/40 text-xs">
                  Products this line already sells stay in your store. Only applies to suppliers
                  set to "Deselect all" — a supplier set to "Select all" sells everything anyway.
                </p>
              )}
            </div>

            <div className="flex flex-col gap-2">
              <label className="flex items-center gap-2 text-sm text-white/70">
                <input
                  type="checkbox"
                  checked={editing.footwear_only}
                  onChange={(e) => update({ footwear_only: e.target.checked })}
                />
                Footwear only (skips apparel, balls and bags a supplier files under the same brand)
              </label>
              {!editing.id && (
                <label className="flex items-center gap-2 text-sm text-white/70">
                  <input
                    type="checkbox"
                    checked={editing.create_collection !== false}
                    onChange={(e) => update({ create_collection: e.target.checked })}
                  />
                  Create a collection for this tag
                </label>
              )}
            </div>

            {/* Preview */}
            <div className="rounded-lg border border-white/10 p-3 min-h-[4rem]">
              {!editing.brand && editing.patterns.length === 0 ? (
                <p className="text-white/40 text-sm">Pick a brand or type a model to see what matches.</p>
              ) : previewError ? (
                <p className="text-red-400 text-sm">{previewError}</p>
              ) : !preview ? (
                <p className="text-white/40 text-sm">Matching…</p>
              ) : (
                <div className={`space-y-2 ${previewing ? 'opacity-60' : ''}`}>
                  <p className="text-white text-sm">
                    <span className="font-semibold">{preview.products}</span> products
                    {(() => {
                      // Only worth spelling out when not every match is simply added.
                      const b = readBreakdown(preview.breakdown);
                      return b && b.added !== preview.products ? (
                        <span className="text-white/70" title={b.in_store ? IN_STORE_HINT : undefined}>
                          {' '}({describeLineCounts(b, preview.products)})
                        </span>
                      ) : null;
                    })()}
                    {preview.per_provider.length > 0 && (catalog ? (
                      <span className="text-white/50">
                        {' '}at {preview.per_provider.length} supplier{preview.per_provider.length === 1 ? '' : 's'}
                        {' '}— most at {preview.per_provider.slice(0, 3).map((p) => `${p.name} (${p.products})`).join(', ')}
                      </span>
                    ) : (
                      <span className="text-white/50">
                        {' '}— {preview.per_provider.map((p) => `${p.name} ${p.products}`).join(', ')}
                      </span>
                    ))}
                  </p>
                  <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
                    {preview.sample.map((s) => (
                      <div key={s.sku} className="text-[10px] text-white/60" title={s.title}>
                        {s.photo_url ? (
                          <img
                            src={s.photo_url}
                            alt=""
                            className="w-full aspect-square object-cover rounded bg-white/5"
                            // Some suppliers' photo links expire (JKN signs them); show the empty tile, not a broken image.
                            onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }}
                          />
                        ) : (
                          <div className="w-full aspect-square rounded bg-white/5" />
                        )}
                        <div className="truncate mt-1">{s.title}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setEditing(null)}
                disabled={saving}
                className="px-4 py-2 text-sm text-white/70 border border-white/15 rounded hover:bg-white/[0.08] disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={save}
                disabled={saving || !editing.name.trim() || !editing.tag.trim() || (!editing.brand && editing.patterns.length === 0)}
                className="px-4 py-2 text-sm bg-orange-600 text-white rounded hover:bg-orange-700 disabled:opacity-50"
              >
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>,
        document.body,
      )}

      {/* Delete confirmation */}
      {pendingDelete && createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
          <div className="glass-card rounded-xl p-6 max-w-md mx-4 space-y-4">
            <h3 className="text-white font-semibold text-lg">Remove "{pendingDelete.name}"?</h3>
            <ul className="text-white/60 text-sm space-y-1 list-disc pl-5">
              <li>Products lose the "{pendingDelete.tag}" tag on the next sync.</li>
              <li>
                From suppliers set to "Deselect all", products no other line or hand-pick covers
                are set to 0 stock and drafted. They come back if you add the line again.
              </li>
              <li>The collection stays in your store; delete it there if you no longer want it.</li>
            </ul>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setPendingDelete(null)}
                className="px-4 py-2 text-sm text-white/70 border border-white/15 rounded hover:bg-white/[0.08]"
              >
                Cancel
              </button>
              <button onClick={confirmDelete} className="px-4 py-2 text-sm bg-red-600 text-white rounded hover:bg-red-700">
                Remove
              </button>
            </div>
          </div>
        </div>,
        document.body,
      )}

      {/* Mode confirmation, with the real numbers */}
      {modeConfirm && createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
          <div className="glass-card rounded-xl p-6 max-w-md mx-4 space-y-4">
            <h3 className="text-white font-semibold text-lg">
              {modeConfirm.mode === 'deselect_all' ? 'Sell only your product lines?' : 'Sell everything?'}
            </h3>
            {modeConfirm.providers_changing === 0 ? (
              <p className="text-white/70 text-sm">Every supplier is already set this way.</p>
            ) : (
              <ul className="text-white/70 text-sm space-y-1 list-disc pl-5">
                <li>{modeConfirm.providers_changing} supplier{modeConfirm.providers_changing === 1 ? '' : 's'} change.</li>
                <li>
                  Products on sale: {modeConfirm.products_selected_now.toLocaleString()} →{' '}
                  {modeConfirm.products_selected_after.toLocaleString()}.
                </li>
                {modeConfirm.products_removed > 0 && (
                  <li className="text-amber-300">
                    {modeConfirm.products_removed.toLocaleString()} products (
                    {modeConfirm.units_removed.toLocaleString()} supplier units) stop selling: set to
                    0 stock and drafted on the next sync.
                  </li>
                )}
                {modeConfirm.products_added > 0 && (
                  <li>{modeConfirm.products_added.toLocaleString()} products become available to import.</li>
                )}
              </ul>
            )}
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setModeConfirm(null)}
                className="px-4 py-2 text-sm text-white/70 border border-white/15 rounded hover:bg-white/[0.08]"
              >
                Cancel
              </button>
              {modeConfirm.providers_changing > 0 && (
                <button
                  onClick={applyMode}
                  className={`px-4 py-2 text-sm text-white rounded ${modeConfirm.products_removed > 0 ? 'bg-red-600 hover:bg-red-700' : 'bg-orange-600 hover:bg-orange-700'}`}
                >
                  Confirm
                </button>
              )}
            </div>
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
};

export default ProductLines;
